import { prisma } from "client";

export const ORGANIZATION_OWNER_ROLE_NAME = "ORGANIZATION_OWNER";

export const ORGANIZATION_OWNER_CONFLICT_MESSAGE =
  "We should have only 1 organization owner. This organization already has an owner assigned.";

type OrganizationRef =
  | { id?: string | { $oid?: string } | null; _id?: string | { $oid?: string } | null }
  | string
  | null
  | undefined;

type UserOrganizationSource = {
  organizations?: OrganizationRef[];
  organizationIds?: Array<string | { $oid?: string } | null | undefined>;
};

function normalizeMongoId(id: unknown): string | undefined {
  if (!id) return undefined;
  if (typeof id === "string") return id;
  if (typeof id === "object" && "$oid" in id) {
    return (id as { $oid?: string }).$oid;
  }
  return undefined;
}

function normalizeMongoIdArray(ids: unknown[] | undefined): string[] {
  return (
    ids
      ?.map((id) => normalizeMongoId(id))
      .filter((id): id is string => Boolean(id)) ?? []
  );
}

export const getExistingUserOrganizationIds = (
  user?: UserOrganizationSource | null
): string[] => {
  const fromOrganizations =
    user?.organizations
      ?.map((org) =>
        typeof org === "string"
          ? normalizeMongoId(org)
          : normalizeMongoId(org?.id ?? org?._id)
      )
      .filter((id): id is string => Boolean(id)) ?? [];

  const fromIds =
    user?.organizationIds
      ?.map((id) => normalizeMongoId(id))
      .filter((id): id is string => Boolean(id)) ?? [];

  return Array.from(new Set([...fromOrganizations, ...fromIds]));
};

export const getOrganizationIdsForOwnerCheck = (options: {
  assignmentOrganizationIds?: string[];
  organizationId?: string;
  selectedOrganizerIds?: string[];
  existingUserOrganizationIds?: string[];
}): string[] => {
  const {
    assignmentOrganizationIds = [],
    organizationId,
    selectedOrganizerIds = [],
    existingUserOrganizationIds = [],
  } = options;

  return Array.from(
    new Set(
      [
        ...assignmentOrganizationIds,
        ...selectedOrganizerIds,
        ...(organizationId ? [organizationId] : []),
        ...existingUserOrganizationIds,
      ]
        .map((id) => normalizeMongoId(id))
        .filter((id): id is string => Boolean(id))
    )
  );
};

export const resolveOrganizationIdsForOwnerAssignment = (options: {
  selectedOrganizerIds?: string[];
  organizationId?: string;
  existingUserOrganizationIds?: string[];
}): string[] => {
  const selectedOrganizerIds = normalizeMongoIdArray(options.selectedOrganizerIds);
  if (selectedOrganizerIds.length > 0) {
    return selectedOrganizerIds;
  }

  const organizationId = normalizeMongoId(options.organizationId);
  if (organizationId) {
    return [organizationId];
  }

  return options.existingUserOrganizationIds ?? [];
};

type OrganizationOwnerPrismaClient = Pick<typeof prisma, "roleV2" | "user">;

export const assertSingleOrganizationOwner = async (
  roleV2Id: string | undefined,
  organizationIds: string[],
  excludeUserId?: string,
  client: OrganizationOwnerPrismaClient = prisma
) => {
  const uniqueOrgIds = [...new Set(organizationIds.filter(Boolean))];
  if (!roleV2Id || uniqueOrgIds.length === 0) return;

  const role = await client.roleV2.findUnique({
    where: { id: roleV2Id },
    select: { name: true },
  });
  if (role?.name !== ORGANIZATION_OWNER_ROLE_NAME) return;

  for (const orgId of uniqueOrgIds) {
    const existingOwner = await client.user.findFirst({
      where: {
        organizations: { some: { id: orgId } },
        roleV2: { name: ORGANIZATION_OWNER_ROLE_NAME },
        ...(excludeUserId ? { id: { not: excludeUserId } } : {}),
      },
      select: { id: true },
    });
    if (existingOwner) {
      throw new Error(ORGANIZATION_OWNER_CONFLICT_MESSAGE);
    }
  }
};
